import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import { nationalGenChart30d, nationalGenChartMonthly, nationalGenChartHourly, nationalKPIs } from '../../data/mockData';

type Tab = 'daily' | 'monthly' | 'hourly';

const tabCls = (active: boolean) =>
  `px-3 py-1.5 text-xs rounded-input border transition-colors ${
    active ? 'bg-navy text-white border-navy' : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
  }`;

// Stat chip
function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-border rounded-card px-4 py-2.5 flex-1 min-w-0">
      <p className="text-xs text-text-secondary font-semibold leading-tight">{label}</p>
      <p className="text-sm font-bold text-text-primary mt-0.5 truncate">{value}</p>
    </div>
  );
}

function variance(actual: number, expected: number) {
  return (((actual - expected) / expected) * 100).toFixed(1);
}

// Custom tooltip
const CustomTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: { value: number; name: string }[];
  label?: string;
}) => {
  if (!active || !payload || !payload.length) return null;
  const actual   = payload.find(p => p.name === 'actual')?.value ?? 0;
  const expected = payload.find(p => p.name === 'expected')?.value ?? 0;
  const ghi      = payload.find(p => p.name === 'ghi')?.value ?? 0;
  const varPct   = variance(actual, expected);

  return (
    <div className="bg-white border border-border rounded-card shadow-lg px-3 py-2 text-xs space-y-1">
      <p className="font-semibold text-text-primary mb-1">{label}</p>
      <p className="text-blue-600">Actual: <span className="font-semibold">{actual} GWh</span></p>
      <p className="text-indigo-500">GHI-Expected: <span className="font-semibold">{expected} GWh</span></p>
      <p className={parseFloat(varPct) < 0 ? 'text-red-500' : 'text-green-600'}>
        Variance: <span className="font-semibold">{parseFloat(varPct) > 0 ? '+' : ''}{varPct}%</span>
      </p>
      <p className="text-amber-500">GHI: <span className="font-semibold">{ghi} W/m²</span></p>
    </div>
  );
};

export default function NationalGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const chartData =
    tab === 'daily'   ? nationalGenChart30d :
    tab === 'monthly' ? nationalGenChartMonthly :
    nationalGenChartHourly;

  const unit = tab === 'monthly' ? 'GWh' : 'GWh';

  // Summary stat computations
  const stats = useMemo(() => {
    const actuals = chartData.map(d => d.actual);
    const total   = actuals.reduce((s, v) => s + v, 0);
    const avg     = total / actuals.length;
    const maxIdx  = actuals.indexOf(Math.max(...actuals));
    const minIdx  = actuals.indexOf(Math.min(...actuals));
    const avgGHI  = Math.round(chartData.reduce((s, d) => s + d.ghi, 0) / chartData.length);

    const fmt = (v: number) => tab === 'monthly' ? `${Math.round(v)} GWh` : `${v.toFixed(1)} GWh`;

    return {
      total:   tab === 'monthly' ? `${Math.round(total)} GWh` : `${total.toFixed(1)} GWh`,
      avg:     fmt(avg),
      best:    `${chartData[maxIdx]?.date} · ${fmt(actuals[maxIdx])}`,
      worst:   `${chartData[minIdx]?.date} · ${fmt(actuals[minIdx])}`,
      avgGHI:  `${avgGHI} W/m²`,
    };
  }, [chartData, tab]);

  const periodLabel =
    tab === 'daily'   ? 'Last 30 days' :
    tab === 'monthly' ? 'Last 12 months' :
    'Today · 15-min intervals';

  return (
    <div className="p-6 space-y-5">
      {/* ── 6 KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Today's Generation"          value="36.0 GWh"                                            subtext="+6.2% vs yesterday"       subtextColor="green" />
        <KPICard title="This Month"                  value="892 GWh"                                             subtext="Apr 2026" />
        <KPICard title="Lifetime Total"              value="4.7 TWh"                                             subtext="Since platform launch" />
        <KPICard title="Peak Power Today"            value="40.2 GW"                                             subtext="At 12:00 PM" />
        <KPICard title="AVG SPECIFIC YIELD TODAY"    value={`${nationalKPIs.avgSpecificYield} kWh/kWp`}          subtext="National average" />
        <KPICard title="AVG PERFORMANCE RATIO TODAY" value={`${nationalKPIs.avgPR}%`}                            subtext="National average" />
      </div>

      {/* ── Summary stat row ── */}
      <div className="flex gap-3">
        <StatChip label="Period total" value={stats.total} />
        <StatChip label="Daily average" value={stats.avg} />
        <StatChip label="Best day" value={stats.best} />
        <StatChip label="Worst day" value={stats.worst} />
        <StatChip label="Avg GHI" value={stats.avgGHI} />
      </div>

      {/* ── Main combo chart ── */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <SectionDivider title="Generation vs GHI-Expected" subtitle={periodLabel} />
          </div>
          <div className="flex items-center gap-1.5">
            <button className={tabCls(tab === 'daily')}   onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls(tab === 'monthly')} onClick={() => setTab('monthly')}>Monthly</button>
            <button className={tabCls(tab === 'hourly')}  onClick={() => setTab('hourly')}>Today Hourly</button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 48, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: tab === 'daily' ? 9 : 11, fill: '#999' }}
              axisLine={false}
              tickLine={false}
              interval={tab === 'daily' ? 4 : tab === 'hourly' ? 3 : 0}
            />
            {/* Left Y: generation */}
            <YAxis
              yAxisId="gen"
              tick={{ fontSize: 11, fill: '#999' }}
              axisLine={false}
              tickLine={false}
              label={{ value: unit, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11, fill: '#999' } }}
            />
            {/* Right Y: GHI */}
            <YAxis
              yAxisId="ghi"
              orientation="right"
              tick={{ fontSize: 11, fill: '#D97706' }}
              axisLine={false}
              tickLine={false}
              label={{ value: 'W/m²', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11, fill: '#D97706' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="rect"
              iconSize={10}
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(value: string) =>
                value === 'actual'   ? 'Actual Generation' :
                value === 'expected' ? 'GHI-Expected' :
                'Solar Irradiance (GHI)'
              }
            />
            {/* GHI shaded area (behind bars) */}
            <Area
              yAxisId="ghi"
              dataKey="ghi"
              fill="#FEF3C7"
              stroke="#F59E0B"
              strokeWidth={0}
              fillOpacity={0.5}
              dot={false}
              activeDot={false}
            />
            {/* Actual generation bars */}
            <Bar
              yAxisId="gen"
              dataKey="actual"
              fill="#3B82F6"
              radius={[3, 3, 0, 0]}
              maxBarSize={tab === 'daily' ? 16 : 28}
            />
            {/* GHI-expected dotted line */}
            <Line
              yAxisId="gen"
              dataKey="expected"
              stroke="#6366F1"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
